import type { SummaryCardVariant, SummaryDisplayData, SummaryNoviceView } from '~/features/my-wallet/types/summary'
import type { WalletPaymentModel } from '~/store-v2/my-wallet/useWalletPaymentModel'
import { resolveContextNote, toNextModelTarget } from '~/features/my-wallet/utils/context-note'
import { formatUpdatedAt, isWalletBlocked, resolveThresholds, toKnownModelKey } from '~/features/my-wallet/utils/my-wallet'

// Blocked is resolved before this map; any other state falls back to the performance variant.
const SUMMARY_VARIANT_BY_STATE: Record<string, SummaryCardVariant> = {
  ESSENTIAL_PROFILE: 'essential',
  NOVICE: 'novice',
}

function resolveSummaryVariant(summary: WalletPaymentModel['summary']): SummaryCardVariant {
  if (isWalletBlocked(summary))
    return 'blocked'
  return SUMMARY_VARIANT_BY_STATE[summary.state] ?? 'performance'
}

// The required total only exists in `eligibility_context`; without it this returns null rather than "x of 0".
function buildNoviceView(paymentModel: WalletPaymentModel): SummaryNoviceView | null {
  const eligibilityContext = paymentModel.eligibility_context
  if (!eligibilityContext)
    return null
  const completed = paymentModel.summary.novice_completed ?? eligibilityContext.novice_completed
  return {
    completed,
    required: eligibilityContext.novice_required,
    isDone: completed >= eligibilityContext.novice_required,
  }
}

export function buildSummaryView(paymentModel: WalletPaymentModel): SummaryDisplayData {
  const { summary } = paymentModel
  const variant = resolveSummaryVariant(summary)
  const hasNoCompletions = summary.total_completed === 0

  return {
    variant,
    updatedAt: formatUpdatedAt(summary.updated_at),
    currentModel: toKnownModelKey(summary.current_model),
    upfrontPercentage: summary.upfront_percentage,
    afterCompletionPercentage: summary.after_completion_percentage,
    percentage: summary.performance_percentage ?? paymentModel.performance?.on_time_percentage ?? null,
    hasNoCompletions,
    thresholds: resolveThresholds(paymentModel),
    novice: buildNoviceView(paymentModel),
    contextNote: variant === 'performance'
      ? resolveContextNote({
          summary,
          hasCommercialException: summary.state === 'COMMERCIAL_EXCEPTION',
          hasNoCompletions,
          nextModel: toNextModelTarget(summary.next_model, summary.projects_needed_for_next_model),
        })
      : null,
  }
}
